import { defineStore } from 'pinia'
import { type PlanCheckup, PlanType } from '~/generated/api-clients-generated'
import dayjs from 'dayjs'
import { ProfileStore } from '~/stores/profile'
import { ApplicationStore } from '~/stores/applications'

export enum PlanCheckupPhase {
  Inactive,
  Oep,
  PreAep,
  DuringAep
}

export enum PlanCheckupStatus {
  Incomplete,
  Ready,
  BetterPlan,
  SamePlan,
  NoPlan,
  PdpUpsell
}

export class PlanCheckupStore {
  static use = defineStore('planCheckup', () => {
    const applications = ApplicationStore.use()

    const planCheckups = ref<Array<PlanCheckup>>([])
    const sync = ref(false)

    const { flag } = FeatureFlags.use()

    const isOep = computed(() => flag<boolean>('is-oep').value)

    const currentPlanYear = computed(() => dayjs().year())
    const targetPlanYear = computed(() =>
      isOep.value ? currentPlanYear.value : currentPlanYear.value + 1
    )

    const currentCheckup = computed(() =>
      planCheckups.value.find(
        (pc) => pc.planYear === targetPlanYear.value && pc.type === (isOep.value ? 'oep' : 'aep')
      )
    )

    const nextYearPolicy = computed(() => {
      return applications.applications.find(
        (app) =>
          app.planYear === targetPlanYear.value &&
          !!app.planType &&
          [PlanType.MAPD, PlanType.PDP].includes(app.planType)
      )
    })

    const status = computed<PlanCheckupStatus>(() => {
      if (_isNil(currentCheckup.value)) return PlanCheckupStatus.Incomplete

      if (_isNil(currentCheckup.value?.bestPlanId) || phase.value === PlanCheckupPhase.PreAep)
        return PlanCheckupStatus.Ready

      if (samePlanRecommended.value) return PlanCheckupStatus.SamePlan

      if (
        currentCheckup.value?.currentPlanType === PlanType.PDP &&
        currentCheckup.value?.desiredPlanType === PlanType.MAPD
      )
        return PlanCheckupStatus.PdpUpsell

      return _isNil(currentCheckup.value?.currentPlanId)
        ? PlanCheckupStatus.NoPlan
        : PlanCheckupStatus.BetterPlan
    })

    const createCheckup = async () => {
      const { createPlanCheckupClient } = PxApi.use()
      const client = createPlanCheckupClient()

      const apps = ApplicationStore.use()
      const profile = ProfileStore.use()

      const currentPlan = computed(
        () => profile.needsAssessment.currentPlan?.[currentPlanYear.value.toString()]
      )

      const currentPolicy = apps.applications.find(
        (a) =>
          a.planYear === currentPlanYear.value &&
          [PlanType.PDP, PlanType.MAPD].includes(a.planType!) &&
          a.status === 'approved'
      )

      const usedPlan = computed(() => ({
        medicareId: currentPlan.value ?? currentPolicy?.medicareId,
        type: !!currentPlan.value ? null : currentPolicy?.planType //TODO probably shouldn't pass null here?
      }))

      const guid = await client.createPlanCheckup(
        targetPlanYear.value,
        usedPlan.value.medicareId,
        usedPlan.value.type
      )

      planCheckups.value.push({
        id: guid,
        planYear: targetPlanYear.value,
        currentPlanId: usedPlan.value.medicareId,
        currentPlanType: usedPlan.value.type,
        type: isOep.value ? 'oep' : 'aep'
      })
    }

    const updateCheckup = async (updates: Partial<PlanCheckup>) => {
      if (_isNil(currentCheckup.value)) {
        throw new Error('updateCheckup called with no current plan checkup')
      }

      const checkup = { ...currentCheckup.value, ...updates }

      const { createPlanCheckupClient } = PxApi.use()
      const client = createPlanCheckupClient()

      await client.updatePlanCheckup(checkup.id!, checkup)

      planCheckups.value = [...planCheckups.value.filter((pc) => pc.id !== checkup.id), checkup]
    }

    function init(ls: Array<PlanCheckup> | null = []) {
      planCheckups.value = [...(ls ?? [])]
      sync.value = true
    }

    const checkupEnabled = computed(() => flag<boolean>('plan-checkup').value)

    const recsReady = computed(() => flag<boolean>('next-year-recommendations-enabled').value)

    const samePlanRecommended = computed(
      () =>
        !_isNil(currentCheckup.value?.bestPlanId) &&
        currentCheckup.value?.bestPlanId === currentCheckup.value.currentPlanId
    )

    const phase = computed<PlanCheckupPhase>(() => {
      if (!checkupEnabled.value) return PlanCheckupPhase.Inactive
      if (isOep.value) return PlanCheckupPhase.Oep
      if (!recsReady.value) return PlanCheckupPhase.PreAep
      return PlanCheckupPhase.DuringAep
    })

    const contentSuffix = computed(() =>
      !!phase.value ? PlanCheckupPhase[phase.value].toString() : null
    )

    const contentPropertyKey = computed(() =>
      _snakeCase(PlanCheckupStatus[status.value].toString())
    )

    return {
      sync,
      init,
      planCheckups,
      currentCheckup,
      createCheckup,
      updateCheckup,
      status,
      recsReady,
      checkupEnabled,
      samePlanRecommended,
      phase,
      contentSuffix,
      contentPropertyKey,
      nextYearPolicy,
      targetPlanYear
    }
  })

  static init(ls: Array<PlanCheckup> | null = []) {
    const store = PlanCheckupStore.use()
    store.init(ls)
  }
}
